// frontend/src/App.jsx
import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

// USER PAGES
import Visualizer from "./components/visualizer/visualizer.jsx";
import Devices from "./components/devices/devices.jsx";
import DeviceDetail from "./components/devices/deviceControl.jsx/deviceControl.jsx";
import ScreenViewer from "./components/screen/ScreenViewer.jsx";
import Issues from "./components/issues/issues.jsx";
import Scan from "./components/scan/scan.jsx";
import TaskManager from "./components/devices/Taskmanager/taskmanager.jsx";
import InstalledApps from "./components/devices/installedApps/installedapps.jsx";
import RoleAccounts, { MyProfileView } from "./components/profile/RoleAccounts.jsx";
import Performance from "./components/devices/Taskmanager/Performance/Performance.jsx";
import DashboardHub from "./components/dashboard/DashboardHub.jsx";
import AllAttendance from "./components/analytics/AllAttendance.jsx";
import EmployeeAnalytics from "./components/analytics/EmployeeAnalytics.jsx";
import CFOSalaryConfig from "./components/analytics/CFOSalaryConfig.jsx";
import ProductivityConfig from "./components/productivity/ProductivityConfig.jsx";
import ScreenSessions from "./components/ScreenSessions/screenSessions.jsx";
import MonitoringHub from "./components/monitoring/MonitoringHub.jsx";
import VisunAi from "./components/visunAi/VisunAi.jsx";
import BillingPage from "./components/billing/BillingPage.jsx";
import TenantLicenseManager from "./components/devices/TenantLicenseManager.jsx";
import BillingPlanDetail from "./components/billing/BillingPlanDetail.jsx";
import BillingPaymentDetail from "./components/billing/BillingPaymentDetail.jsx";
import GuidePage from "./components/guide/Guide.jsx";
import InstallGuide from "./components/guide/InstallGuide.jsx";

// AUTH & ADMIN
import Login from "./components/navigation/Login.jsx";
import SsoCallback from "./components/navigation/SsoCallback.jsx";
import UpdatePassword from "./components/navigation/UpdatePassword.jsx";
import CreateUser from "./components/admin/CreateUser.jsx";
import AdminDashboard from "./components/admin/AdminDashboard.jsx";
import AdminBillingOverview from "./components/admin/AdminBillingOverview.jsx";
import AdminBillingTenantDetail from "./components/admin/AdminBillingTenantDetail.jsx";
import AdminTenantWorkspace from "./components/admin/AdminTenantWorkspace.jsx";
import AdminBillingDetail from "./components/admin/AdminBillingDetail.jsx";
import AdminTenantBilling from "./components/admin/AdminTenantBilling.jsx";
import ManageTenants from "./components/admin/ManageTenants.jsx";
import LicenseManager from "./components/admin/LicenseManager.jsx";
import PriceManager from "./components/admin/PriceManager.jsx";
import AdminLayout from "./components/admin/AdminLayout.jsx";
import BlockedDomains from "./components/admin/BlockedDomains.jsx";
// import ManageUsers from "./components/admin/ManageUsers.jsx"; // create later

// PUBLIC PAGES
import Download from "./components/download/Download.jsx";
import PagesIndex from "./components/pages/PagesIndex.jsx";
import TermsAndConditions from "./components/pages/TermsAndConditions.jsx";
import PrivacyPolicy from "./components/pages/PrivacyPolicy.jsx";
import LandingPage from "./components/landing/LandingPage.jsx";
import ClientsPage from "./components/clients/ClientsPage.jsx";

// TOKEN HELPERS
import { getToken, getRole, logoutUser, syncSessionProfile, needsRoleAssignment, needsAllocation } from "./utils/authService.js";

// LAYOUT WRAPPER
import Layout from "./components/navigation/Layout.jsx";
import { apiGet } from "./utils/api.js";
import { Toaster } from "./utils/toast.jsx";
import "./utils/toast.css";
import SupportBot from "./components/support/SupportBot.jsx";

// ------------------------------------------------------
// 🔐 PROTECTED ROUTES
// ------------------------------------------------------

// Block access if no token
function ProtectedRoute({ children }) {
  return getToken() ? children : <Navigate to="/login" replace />;
}

function getAllowedViews() {
  try {
    const raw = JSON.parse(sessionStorage.getItem("allowedViews") || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

const VIEW_TO_PATH = {
  visualizer: "/visualizer",
  devices: "/devices",
  // All four dashboard views now converge on the merged hub — see DashboardHub.jsx,
  // which picks the right tab per role/allowedViews itself.
  executive_dashboard: "/dashboard",
  cfo_dashboard: "/dashboard",
  employee_dashboard: "/dashboard",
  manager_dashboard: "/dashboard",
  download: "/download",
  visun_ai: "/visun-ai",
  productivity_config: "/settings",
  billing: "/billing",
  guide: "/guide",
};

const FEATURE_ACCESS_ALIASES = {
  tasks: "devices",
  apps: "devices",
  performance: "devices",
  scan: "monitoring",
  usb: "monitoring",
  urltracker: "monitoring",
  filemonitor: "monitoring",
  fileuploads: "monitoring",
};

function resolveRequiredFeatureId(featureId) {
  const normalized = String(featureId || "").trim().toLowerCase();
  if (!normalized) return "";
  return FEATURE_ACCESS_ALIASES[normalized] || normalized;
}

function buildUnlockedFeatureMap(unlockedFeatureIds = []) {
  const unlockedMap = {};
  if (Array.isArray(unlockedFeatureIds)) {
    unlockedFeatureIds.forEach((id) => {
      const normalizedId = String(id || "").trim();
      if (normalizedId) unlockedMap[normalizedId] = true;
    });
  }
  return unlockedMap;
}

function getDefaultRouteByViews(views = []) {
  const preferredOrder = [
    "visun_ai",
    "executive_dashboard",
    "cfo_dashboard",
    "manager_dashboard",
    "devices",
    "visualizer",
    "billing",
    "download",
    "employee_dashboard",
    "profile",
  ];
  for (const key of preferredOrder) {
    if (views.includes(key) && VIEW_TO_PATH[key]) return VIEW_TO_PATH[key];
  }
  return "/role-accounts";
}

function getDefaultRouteByRole(role) {
  // A brand-new SSO-JIT account (see User.js's ssoRoleAssigned) only ever got the tenant's bare
  // default role automatically — nobody actually decided this person should have it. Send them
  // to the empty "no role assigned" profile page instead of that default role's real dashboard,
  // until the tenant admin explicitly assigns something via RoleAccounts.jsx.
  if (needsRoleAssignment()) return "/role-accounts";
  // A role was deliberately set (mapped or manually assigned), but employee/manager still needs
  // a device/team linked before their dashboard means anything — see User.js's ssoNeedsAllocation.
  if (needsAllocation()) return "/role-accounts";
  if (role === "admin") return "/admin/dashboard";
  if (role === "client") {
    // Download accounts (seeded per-tenant for grabbing agent installers) land
    // on the download page itself; everyone else lands on the merged dashboard
    // hub by default now (previously /devices).
    return sessionStorage.getItem("displayRole") === "Download Account" ? "/download" : "/dashboard";
  }
  // Scoped roles must always land on their own dashboard by default. Extra
  // allowedViews only add sidenav items — they must never hijack the post-login
  // landing page (previously checked first, so granting e.g. "devices" access to
  // a manager would send them to /devices instead of their dashboard).
  // All four now converge on the merged hub — DashboardHub.jsx picks the
  // right (and only, for a scoped role) tab itself.
  if (role === "analytics_viewer" || role === "ceo") return "/dashboard";
  if (role === "cfo") return "/dashboard";
  if (role === "employee") return "/dashboard";
  if (role === "manager") return "/dashboard";

  const allowedViews = getAllowedViews();
  if (allowedViews.length > 0) return getDefaultRouteByViews(allowedViews);
  return "/role-accounts";
}

function isPathAllowedForRole(role, path) {
  if (!role) return false;
  // Mirrors the getDefaultRouteByRole gate above — while true, only the empty "no role assigned"
  // page is reachable, even by typing another URL directly. Never applies to admin/client (never
  // JIT-created) but checked first regardless, for the same reason it's checked first there.
  // /profile (the dedicated TopNav Profile link, see RoleAccounts.jsx's MyProfileView) stays
  // reachable in both pending states below — it's read-only account info, not the gated
  // dashboard experience, so there's nothing to actually withhold by blocking it.
  if (needsRoleAssignment()) return path === "/role-accounts" || path === "/profile";
  // Waiting on device/team allocation still shouldn't block the one self-service action
  // available to them: grabbing the agent installer (see sidenav.jsx's isSsoAccount gate
  // for why this is safe to leave unconditional here — ssoNeedsAllocation is only ever
  // true for SSO-JIT accounts to begin with, never a manually-created one).
  if (needsAllocation()) return path === "/role-accounts" || path === "/download" || path === "/profile";
  if (role === "admin" || role === "client") return true;

  // Profile, License Manager, and Attendance are always accessible for any
  // authenticated non-admin role — they're alwaysShow sidebar items with no
  // view-key gating. The employee drill-down is only ever reached by clicking a
  // row on the Attendance page, so it follows the same unconditional rule as its
  // parent rather than the "monitoring" allowedViews key below — otherwise a
  // manager (whose default allowedViews has no "monitoring" grant) could open
  // Attendance but bounce straight back to their dashboard on every row click.
  // Guide and Plans & Billing are NOT universal: a role only gets them if its
  // template explicitly grants the "guide"/"billing" view, same as any other
  // selectable view (see the `map` lookup below).
  if (
    path === "/role-accounts"
    || path === "/profile"
    || path === "/license"
    || path === "/all-attendance"
    || path.startsWith("/analytics/employee/")
  ) return true;
  const SCOPED_ROLES = new Set(["employee", "manager", "ceo", "cfo", "analytics_viewer"]);
  if (path === "/role-dashboard" && SCOPED_ROLES.has(role)) return true;

  // Each scoped role's own intrinsic dashboard must always stay reachable, no matter
  // what extra allowedViews a role template grants on top. Previously this only held
  // when allowedViews was empty — granting one unrelated extra view (e.g. "devices")
  // to a manager via a role template silently locked them out of their own dashboard
  // and caused a redirect loop, since getDefaultRouteByRole still pointed them there.
  // Old individual dashboard routes now just redirect into /dashboard (see the Route
  // table), but the checks below stay as a second line of defense.
  if (role === "employee" && path === "/employee-dashboard") return true;
  if (role === "manager" && path === "/manager-dashboard") return true;
  if ((role === "ceo" || role === "analytics_viewer") && path === "/analytics") return true;
  if (role === "cfo" && path === "/cfo-dashboard") return true;
  // The merged dashboard hub itself: always reachable for any scoped role — DashboardHub
  // filters its own tabs from allowedViews/unlockedFeatures and shows an empty state if
  // the role genuinely has none, rather than needing a route-level gate here.
  if (path === "/dashboard" && SCOPED_ROLES.has(role)) return true;

  const allowedViews = getAllowedViews();

  // Salary config is reachable from either CFO Dashboard or the Productivity Config
  // page's Salaries quick-link, so it accepts either grant — unlike /cfo-dashboard,
  // which stays strictly cfo_dashboard-only.
  if (path === "/cfo-salary-config") {
    return allowedViews.includes("cfo_dashboard") || allowedViews.includes("productivity_config");
  }

  const map = [
    { key: "visualizer", match: (p) => p === "/visualizer" },
    {
      key: "devices",
      match: (p) =>
        p === "/devices" ||
        p.startsWith("/devices/") ||
        p.startsWith("/tasks/") ||
        p.startsWith("/apps/") ||
        p.startsWith("/performance/") ||
        p.startsWith("/screen/"),
    },
    { key: "executive_dashboard", match: (p) => p === "/analytics" },
    { key: "cfo_dashboard", match: (p) => p === "/cfo-dashboard" },
    { key: "employee_dashboard", match: (p) => p === "/employee-dashboard" || (p === "/role-dashboard" && getRole() === "employee") },
    { key: "manager_dashboard", match: (p) => p === "/manager-dashboard" || (p === "/role-dashboard" && getRole() === "manager") },
    {
      key: "monitoring",
      match: (p) =>
        p === "/monitoring" ||
        p === "/scan" ||
        p === "/usb" ||
        p === "/url-tracker" ||
        p === "/all-attendance" ||
        p === "/file-monitor" ||
        p === "/file-uploads",
    },
    { key: "visun_ai", match: (p) => p === "/visun-ai" },
    { key: "billing", match: (p) => p === "/billing" },
    { key: "guide", match: (p) => p === "/guide" || p === "/guide/installation" },
    { key: "download", match: (p) => p === "/download" },
    { key: "profile", match: (p) => p === "/role-accounts" },
    { key: "productivity_config", match: (p) => p === "/settings" },
  ];

  const required = map.find((r) => r.match(path));
  if (!required) return false;
  return allowedViews.includes(required.key);
}

// Full Layout with Sidebar and TopNav
function ProtectedLayout({ children, adminOnly = false, noSidebar = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const userRole = getRole();
  const [authSyncReady, setAuthSyncReady] = React.useState(false);
  const [, setSessionNonce] = React.useState(0);

  React.useEffect(() => {
    const handleSessionExpired = () => {
      logoutUser();
      navigate("/login", { replace: true });
    };

    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => window.removeEventListener("auth:session-expired", handleSessionExpired);
  }, [navigate]);

  React.useEffect(() => {
    let isActive = true;

    const syncSession = async () => {
      if (!getToken()) {
        if (isActive) setAuthSyncReady(true);
        return;
      }

      try {
        const response = await apiGet("/api/auth/me");
        if (response.status === 401 || response.status === 403) {
          window.dispatchEvent(new CustomEvent("auth:session-expired"));
          return;
        }
        if (!response.ok) return;

        const profile = await response.json().catch(() => null);
        if (!profile) return;

        const changed = syncSessionProfile(profile);
        if (changed && isActive) {
          setSessionNonce((value) => value + 1);
        }
      } catch {
        // Ignore short-lived sync errors; normal API flows still enforce auth.
      } finally {
        if (isActive) setAuthSyncReady(true);
      }
    };

    void syncSession();
    const handleFocusSync = () => {
      void syncSession();
    };
    const handleVisibilitySync = () => {
      if (document.visibilityState === "visible") {
        void syncSession();
      }
    };
    const interval = setInterval(() => {
      void syncSession();
    }, 2000);
    window.addEventListener("focus", handleFocusSync);
    document.addEventListener("visibilitychange", handleVisibilitySync);

    return () => {
      isActive = false;
      clearInterval(interval);
      window.removeEventListener("focus", handleFocusSync);
      document.removeEventListener("visibilitychange", handleVisibilitySync);
    };
  }, []);

  if (getToken() && !authSyncReady) {
    return null;
  }

  if (!getToken()) {
    return <Navigate to="/login" replace />;
  }

  const requiresPasswordReset = sessionStorage.getItem("requiresPasswordReset") === "1";
  const selfServiceRoles = new Set(["admin", "client"]);
  if (
    requiresPasswordReset &&
    selfServiceRoles.has(userRole) &&
    location.pathname !== "/update-password"
  ) {
    return <Navigate to="/update-password" replace />;
  }

  if (!isPathAllowedForRole(userRole, location.pathname)) {
    return <Navigate to={getDefaultRouteByRole(userRole)} replace />;
  }

  // Keep sidebar available for flexible navigation-based accounts.
  const hideSidebar = noSidebar;

  const content = adminOnly ? <AdminRoute>{children}</AdminRoute> : children;

  return (
    <ProtectedRoute>
      <Layout noSidebar={hideSidebar}>{content}</Layout>
    </ProtectedRoute>
  );
}

// Block access if not admin
function AdminRoute({ children }) {
  return getRole() === "admin" ? (
    children
  ) : (
    <Navigate to={getDefaultRouteByRole(getRole())} replace />
  );
}

// Block access if feature is lead-locked
function FeatureGate({ children, featureId }) {
  const [unlockedFeatures, setUnlockedFeatures] = React.useState({});
  const [checking, setChecking] = React.useState(true);
  const requiredFeatureId = React.useMemo(() => resolveRequiredFeatureId(featureId), [featureId]);

  // Re-check on every mount to ensure fresh state from DB
  React.useEffect(() => {
    let isMounted = true;
    const verifyAccess = async () => {
      try {
        const response = await apiGet("/api/features");
        if (response.ok && isMounted) {
          const data = await response.json();
          setUnlockedFeatures(buildUnlockedFeatureMap(data?.unlockedFeatures));
        }
      } catch (err) {
        console.error("Access verification failed:", err);
      } finally {
        if (isMounted) setChecking(false);
      }
    };

    const handleFeatureUpdate = (event) => {
      const nextMap = event?.detail?.unlockedFeatures;
      if (nextMap && typeof nextMap === "object") {
        setUnlockedFeatures(nextMap);
        setChecking(false);
        return;
      }
      void verifyAccess();
    };

    void verifyAccess();
    window.addEventListener("features:updated", handleFeatureUpdate);
    return () => {
      isMounted = false;
      window.removeEventListener("features:updated", handleFeatureUpdate);
    };
  }, []);

  // Scoped roles have access managed by allowedViews and the path guard,
  // not by feature-unlock billing flags. Only the tenant owner (client) pays
  // for features; role-based sub-accounts inherit access from their allowedViews.
  const role = getRole();
  const ROLE_BYPASS_FEATURE_GATE = new Set(["admin", "employee", "manager", "ceo", "cfo", "analytics_viewer"]);
  if (ROLE_BYPASS_FEATURE_GATE.has(role)) return children;

  if (checking) return null; // or a loading spinner

  if (!requiredFeatureId || !unlockedFeatures[requiredFeatureId]) {
    const fallbackPath = unlockedFeatures.devices
      ? "/devices"
      : unlockedFeatures.monitoring
        ? "/monitoring"
        : "/billing";
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
}

// ------------------------------------------------------
// 🌐 MAIN APP ROUTER
// ------------------------------------------------------
function App() {
  return (
    <BrowserRouter>
      <Toaster />
      {/* Ally — the floating troubleshooting/support shortcut. Renders
          nothing when logged out, but otherwise has no role restriction
          (it's a scripted help flow, not AI chat, so there's no cost/credit
          reason to gate it). Theme toggle is rendered per-page (landing nav
          / topnav / login) instead of globally, to avoid duplicates. */}
      <SupportBot />
      <Routes>
        {/* PUBLIC LANDING PAGE */}
        <Route path="/" element={<LandingPage />} />

        {/* LOGIN */}
        {/* /register renders the same <Login/> component, in register mode
            (see Login.jsx's mode initializer) — one registration UI (brand
            panel + theme) instead of two different-looking ones. */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/sso/callback" element={<SsoCallback />} />
        <Route path="/register" element={<Login />} />
        <Route
          path="/update-password"
          element={
            <ProtectedLayout noSidebar>
              <UpdatePassword />
            </ProtectedLayout>
          }
        />
        {/* PROTECTED DOWNLOAD PAGE — accessible regardless of plan/feature-unlock status,
            since grabbing the agent installer is a prerequisite to using any paid feature */}
        <Route
          path="/download"
          element={
            <ProtectedLayout>
              <Download />
            </ProtectedLayout>
          }
        />
        {/* ADMIN ROUTES — full-screen Fluent shell, no sidebar/topnav */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><AdminDashboard /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><AdminTenantWorkspace /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/tenants/:tenantId"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><AdminTenantWorkspace /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/tenants/:tenantId/billing"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><AdminTenantBilling /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/tenants/:tenantId/billing/:billingType/:recordId"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><AdminBillingDetail /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/create-user"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><CreateUser /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/licenses"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><LicenseManager /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/pricing"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><PriceManager /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><BlockedDomains /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/billing-overview"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><AdminBillingOverview /></AdminLayout>
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/billing-overview/:tenantId"
          element={
            <ProtectedLayout adminOnly={true} noSidebar={true}>
              <AdminLayout><AdminBillingTenantDetail /></AdminLayout>
            </ProtectedLayout>
          }
        />
        {/* USER ROUTES */}
        <Route
          path="/visualizer"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="visualizer">
                <Visualizer />
              </FeatureGate>
            </ProtectedLayout>
          }
        />

        <Route
          path="/devices"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="devices">
                <Devices />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        <Route
          path="/devices/:id"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="devices">
                <DeviceDetail />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        <Route
          path="/screen/:id"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="devices">
                <ScreenViewer />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        <Route
          path="/tasks/:id"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="tasks">
                <TaskManager />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        <Route
          path="/apps/:id"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="apps">
                <InstalledApps />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        <Route
          path="/performance/:id"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="performance">
                <Performance />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        <Route
          path="/screen-sessions"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="devices">
                <ScreenSessions />
              </FeatureGate>
            </ProtectedLayout>
          }
        />

        <Route
          path="/all-attendance"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="attendance">
                <AllAttendance />
              </FeatureGate>
            </ProtectedLayout>
          }
        />

        <Route
          path="/analytics/employee/:id"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="monitoring">
                <EmployeeAnalytics />
              </FeatureGate>
            </ProtectedLayout>
          }
        />

        <Route
          path="/license"
          element={
            <ProtectedLayout>
              <TenantLicenseManager />
            </ProtectedLayout>
          }
        />

        <Route path="/features" element={<Navigate to="/billing" replace />} />
        <Route
          path="/billing"
          element={
            <ProtectedLayout>
              <BillingPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/billing/plan/:planId"
          element={
            <ProtectedLayout>
              <BillingPlanDetail />
            </ProtectedLayout>
          }
        />
        <Route
          path="/billing/payment/:paymentId"
          element={
            <ProtectedLayout>
              <BillingPaymentDetail />
            </ProtectedLayout>
          }
        />
        <Route
          path="/scan"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="scan">
                <Scan />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        {/* Merged dashboard hub — Executive/CFO/Team/My Work as permission-filtered
            tabs. Gating happens inside DashboardHub itself (mirrors FeatureGate for
            client, allowedViews for every other role), so no FeatureGate wrapper here. */}
        <Route
          path="/dashboard"
          element={
            <ProtectedLayout>
              <DashboardHub />
            </ProtectedLayout>
          }
        />
        {/* Old individual dashboard routes now redirect into the hub — this keeps
            every existing bookmark, notification deep-link, and internal navigate()
            call working unchanged, just landing on the merged page. */}
        <Route path="/analytics" element={<Navigate to="/dashboard?tab=overview" replace />} />
        <Route path="/cfo-dashboard" element={<Navigate to="/dashboard?tab=cfo" replace />} />
        <Route path="/employee-dashboard" element={<Navigate to="/dashboard?tab=mywork" replace />} />
        <Route path="/manager-dashboard" element={<Navigate to="/dashboard?tab=team" replace />} />
        <Route
          path="/role-dashboard"
          element={<Navigate to={getRole() === "manager" ? "/dashboard?tab=team" : "/dashboard?tab=mywork"} replace />}
        />
        <Route
          path="/cfo-salary-config"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="cfo_dashboard">
                <CFOSalaryConfig />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="settings">
                <ProductivityConfig />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        {/* Merged monitoring hub — URL / File Uploads / File Changes / USB as tabs.
            All four already resolve to the same "monitoring" feature id (see
            FEATURE_ACCESS_ALIASES above), so one FeatureGate covers all of them. */}
        <Route
          path="/monitoring"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="monitoring">
                <MonitoringHub />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        {/* Old individual monitoring routes redirect into the hub — keeps every
            existing bookmark/deep-link/navigate() call working unchanged. */}
        <Route path="/usb" element={<Navigate to="/monitoring?tab=usb" replace />} />
        <Route path="/url-tracker" element={<Navigate to="/monitoring?tab=url" replace />} />
        <Route path="/file-monitor" element={<Navigate to="/monitoring?tab=changes" replace />} />
        <Route path="/file-uploads" element={<Navigate to="/monitoring?tab=uploads" replace />} />
        <Route
          path="/visun-ai"
          element={
            <ProtectedLayout>
              <FeatureGate featureId="ai">
                <VisunAi />
              </FeatureGate>
            </ProtectedLayout>
          }
        />
        <Route
          path="/role-accounts"
          element={
            <ProtectedLayout>
              <RoleAccounts />
            </ProtectedLayout>
          }
        />
        {/* Dedicated personal-profile page — the TopNav "Profile" dropdown item always
            links here, separate from /role-accounts (which shows the full Manage
            Accounts page for admin/client/delegated roles). */}
        <Route
          path="/profile"
          element={
            <ProtectedLayout>
              <MyProfileView />
            </ProtectedLayout>
          }
        />

        <Route
          path="/guide"
          element={
            <ProtectedLayout>
              <GuidePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/guide/installation"
          element={
            <ProtectedLayout>
              <InstallGuide />
            </ProtectedLayout>
          }
        />

        {/* PUBLIC TEXT PAGES — no auth required */}
        <Route path="/pages" element={<PagesIndex />} />
        <Route path="/pages/terms" element={<TermsAndConditions />} />
        <Route path="/pages/privacy" element={<PrivacyPolicy />} />
        {/* legacy redirect */}
        <Route path="/terms" element={<Navigate to="/pages/terms" replace />} />

        {/* PRE-SALES CLIENT DOCS — public */}
        <Route path="/clients" element={<ClientsPage />} />

        {/* DEFAULT REDIRECT */}
        <Route
          path="*"
          element={
            getToken() ? (
              <Navigate to={getDefaultRouteByRole(getRole())} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
